import { useState } from "react";
import { Table, Button, Modal, Form, Input, Select, DatePicker, Popconfirm, message, Space, Tag, Divider } from "antd";
import { Plus } from "lucide-react";
import dayjs from "dayjs";
import { useDocClients, useCertTemplates, useCertTemplateCreate, useCertTemplateUpdate, useCertTemplateDelete, useCertificates, useCertificateCreate, useCertificateDelete } from "../../../../api-hooks/documents";

export default function CertificatesTab({ cid }: { cid: number }) {
  const [msgApi, ctx] = message.useMessage();
  const [tmplForm] = Form.useForm();
  const [certForm] = Form.useForm();
  const [tmplModal, setTmplModal] = useState<{ open: boolean; rec: any }>({ open: false, rec: null });
  const [certModal, setCertModal] = useState(false);
  const [certPage, setCertPage] = useState(1);

  const { data: clients = [] } = useDocClients(cid);
  const { data: templates = [], isLoading: tLoading } = useCertTemplates(cid);
  const { data: certData, isLoading: cLoading } = useCertificates(cid, { page: certPage, limit: 50 });
  const certs = (certData as any)?.data ?? [];
  const certTotal = (certData as any)?.total ?? 0;

  const cb = (msg: string, fn?: () => void) => ({ onSuccess: () => { msgApi.success(msg); fn?.(); }, onError: (e: any) => msgApi.error(e?.message || "Error") });
  const tCreate = useCertTemplateCreate(cid, cb("Template created.", () => setTmplModal({ open: false, rec: null })));
  const tUpdate = useCertTemplateUpdate(cid, cb("Updated.", () => setTmplModal({ open: false, rec: null })));
  const tDel = useCertTemplateDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });
  const cCreate = useCertificateCreate(cid, cb("Certificate issued.", () => setCertModal(false)));
  const cDel = useCertificateDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });

  const tmplCols = [
    { title: "Name", dataIndex: "name", key: "n" },
    { title: "Type", dataIndex: "cert_type", key: "ct" },
    { title: "", key: "act", render: (_: any, r: any) => (
      <Space size="small">
        <Button size="small" onClick={() => { setTmplModal({ open: true, rec: r }); tmplForm.setFieldsValue(r); }}>Edit</Button>
        <Popconfirm title="Delete?" onConfirm={() => tDel.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
      </Space>
    )},
  ];

  const certCols = [
    { title: "Cert #", dataIndex: "cert_number", key: "cn" },
    { title: "Template", dataIndex: "template_name", key: "tn" },
    { title: "Client", dataIndex: "client_name", key: "cl" },
    { title: "Issued", dataIndex: "issued_date", key: "i", render: (v: string) => v ? dayjs(v).format("DD/MM/YY") : "—" },
    { title: "Expiry", dataIndex: "expiry_date", key: "e", render: (v: string) => { if (!v) return "—"; const exp = dayjs(v).isBefore(dayjs()); return <Tag color={exp ? "red" : "green"}>{dayjs(v).format("DD/MM/YY")}{exp ? " EXP" : ""}</Tag>; } },
    { title: "Sent", dataIndex: "sent_at", key: "sa", render: (v: string) => v ? <Tag color="green">Sent</Tag> : <Tag>Not sent</Tag> },
    { title: "", key: "act", render: (_: any, r: any) => (
      <Space size="small">
        {r.pdf_url && <Button size="small" onClick={() => window.open(r.pdf_url, "_blank")}>PDF</Button>}
        <Popconfirm title="Delete?" onConfirm={() => cDel.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
      </Space>
    )},
  ];

  return (
    <div>
      {ctx}
      {/* Templates */}
      <div className="flex justify-between mb-2">
        <h3 className="font-semibold text-slate-700 text-sm">Certificate Templates</h3>
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => { setTmplModal({ open: true, rec: null }); tmplForm.resetFields(); }} style={{ background: "#4c1d95" }}>New Template</Button>
      </div>
      <div className="bg-slate-50 rounded-lg border border-slate-100 mb-5">
        <Table rowKey="id" dataSource={templates as any[]} columns={tmplCols} loading={tLoading} size="small" pagination={false} />
      </div>

      <Divider plain className="my-3">Issued Certificates</Divider>
      <div className="flex justify-end mb-2">
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => { setCertModal(true); certForm.resetFields(); }} style={{ background: "#4c1d95" }}>Issue Certificate</Button>
      </div>
      <Table rowKey="id" dataSource={certs} columns={certCols} loading={cLoading} size="small" pagination={{ current: certPage, pageSize: 50, total: certTotal, onChange: setCertPage }} />

      {/* Template Modal */}
      <Modal open={tmplModal.open} onCancel={() => setTmplModal({ open: false, rec: null })} footer={null} destroyOnClose title={tmplModal.rec ? "Edit Template" : "New Certificate Template"} width={560}>
        <Form form={tmplForm} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => { if (tmplModal.rec) tUpdate.mutate({ id: tmplModal.rec.id, ...vals }); else tCreate.mutate(vals); }}>
          <Form.Item name="name" label="Template Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="cert_type" label="Certificate Type" rules={[{ required: true }]}><Input placeholder="e.g. Gas Safety, NICEIC, COSHH" /></Form.Item>
          <Form.Item name="template_html" label="HTML Template" rules={[{ required: true }]}><Input.TextArea rows={6} placeholder="<html>...</html>" /></Form.Item>
          <Form.Item name="logo_url" label="Logo URL"><Input /></Form.Item>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={tCreate.isPending || tUpdate.isPending}>Save</Button>
            <Button onClick={() => setTmplModal({ open: false, rec: null })}>Cancel</Button>
          </div>
        </Form>
      </Modal>

      {/* Issue Certificate Modal */}
      <Modal open={certModal} onCancel={() => setCertModal(false)} footer={null} destroyOnClose title="Issue Certificate" width={480}>
        <Form form={certForm} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => cCreate.mutate({ ...vals, issued_date: vals.issued_date?.format("YYYY-MM-DD"), expiry_date: vals.expiry_date?.format("YYYY-MM-DD"), issued_by: 1 })}>
          <Form.Item name="template_id" label="Template" rules={[{ required: true }]}><Select options={(templates as any[]).map((t: any) => ({ value: t.id, label: t.name }))} /></Form.Item>
          <Form.Item name="cert_number" label="Certificate Number" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="client_id" label="Client"><Select allowClear options={(clients as any[]).map((c: any) => ({ value: c.id, label: c.name }))} showSearch optionFilterProp="label" /></Form.Item>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="issued_date" label="Issue Date" rules={[{ required: true }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="expiry_date" label="Expiry Date"><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
          </div>
          <Form.Item name="pdf_url" label="PDF URL"><Input /></Form.Item>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={cCreate.isPending}>Issue</Button>
            <Button onClick={() => setCertModal(false)}>Cancel</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
